import React from 'react'
import styled from 'styled-components'
import { useFormikContext } from 'formik'
import { useModal } from 'react-modal-hook'
import { useAlert } from 'react-alert'
import {
    Section,
    SectionTitle,
    Row,
    Column,
    SectionSeparator,
    InfoPhrase,
} from '../../shared/shared.styled'
import PrimaryButton from '../../shared/components/button.component'
import { ConfirmationModal } from '../../shared/components/modals/confirmation.modal'
import { AdminApi } from '../admin.api'
import { CreateVehicleRequest } from '../admin.interfaces'
import {
    AvailabilityEnum,
    MarketplacePlatformEnum,
    MarketplaceListingStatusEnum,
} from '../../shared/shared.enums'
import { VehiclePlatformListing } from '../../shared/shared.models'
import TranslationUtils from '../../shared/utils/translations.utils'
import { Typography, Colors } from '../../configs/styled.config'

const PLATFORMS = [
    MarketplacePlatformEnum.STANDVIRTUAL,
    MarketplacePlatformEnum.OLX,
    MarketplacePlatformEnum.PISCA_PISCA,
]

const POLL_INTERVAL_MS = 4000

// Backend still needs MARKETPLACE_CREDENTIALS_KEY configured before publishing can work
const MARKETPLACE_FEATURE_ENABLED = false

interface PlatformListingsSectionProps {
    vehicleId: string
}

const PlatformListingsSection = ({
    vehicleId,
}: PlatformListingsSectionProps) => {
    const { values } = useFormikContext<CreateVehicleRequest>()
    const alert = useAlert()

    const [listings, setListings] = React.useState<VehiclePlatformListing[]>([])
    const [actionPlatform, setActionPlatform] =
        React.useState<MarketplacePlatformEnum | null>(null)
    const pollTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>()

    const isReadyForSale =
        values.availability === AvailabilityEnum.READY_FOR_SALE

    const getListing = React.useCallback(
        (platform: MarketplacePlatformEnum) =>
            listings.find((listing) => listing.platform === platform),
        [listings]
    )

    const fetchListings = React.useCallback(async () => {
        try {
            const { data } =
                await AdminApi.methods.getPlatformListings(vehicleId)
            setListings(data.platformListings)
            return data.platformListings
        } catch (e) {
            return [] as VehiclePlatformListing[]
        }
    }, [vehicleId])

    React.useEffect(() => {
        fetchListings()
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleId])

    const pollUntilSettled = React.useCallback(() => {
        pollTimeoutRef.current = setTimeout(async () => {
            const updated = await fetchListings()
            const stillPending = updated.some(
                (listing) =>
                    listing.status === MarketplaceListingStatusEnum.PENDING
            )
            if (stillPending) {
                pollUntilSettled()
            }
        }, POLL_INTERVAL_MS)
    }, [fetchListings])

    const publish = React.useCallback(
        async (platform: MarketplacePlatformEnum) => {
            try {
                await AdminApi.methods.publishPlatformListing(
                    vehicleId,
                    platform
                )
                await fetchListings()
                pollUntilSettled()
                alert.success('Pedido de publicação enviado')
            } catch (e) {
                alert.error('Erro ao publicar. Por favor tente mais tarde')
            }
        },
        [vehicleId, fetchListings, pollUntilSettled, alert]
    )

    const unpublish = React.useCallback(
        async (platform: MarketplacePlatformEnum) => {
            try {
                await AdminApi.methods.unpublishPlatformListing(
                    vehicleId,
                    platform
                )
                await fetchListings()
                pollUntilSettled()
                alert.success('Pedido de despublicação enviado')
            } catch (e) {
                alert.error('Erro ao despublicar. Por favor tente mais tarde')
            }
        },
        [vehicleId, fetchListings, pollUntilSettled, alert]
    )

    const [showConfirmationModal, hideConfirmationModal] = useModal(() => {
        if (!actionPlatform) {
            return null
        }
        const listing = getListing(actionPlatform)
        const isPublished =
            listing?.status === MarketplaceListingStatusEnum.PUBLISHED
        const platformLabel =
            TranslationUtils.getMarketplacePlatformTranslation(actionPlatform)
        return (
            <ConfirmationModal
                title={isPublished ? 'Despublicar veículo' : 'Publicar veículo'}
                description={
                    isPublished
                        ? `Tem a certeza que quer despublicar este veículo do ${platformLabel}?`
                        : `Tem a certeza que quer publicar este veículo no ${platformLabel}?`
                }
                onCancel={hideConfirmationModal}
                onClick={() => {
                    hideConfirmationModal()
                    if (isPublished) {
                        unpublish(actionPlatform)
                    } else {
                        publish(actionPlatform)
                    }
                }}
            />
        )
    }, [actionPlatform, listings])

    const handleActionClick = (platform: MarketplacePlatformEnum) => {
        if (!MARKETPLACE_FEATURE_ENABLED) {
            alert.info(
                'A publicação em marketplaces ainda está em desenvolvimento.'
            )
            return
        }
        setActionPlatform(platform)
        showConfirmationModal()
    }

    if (!isReadyForSale) {
        return null
    }

    return (
        <Section>
            <Row>
                <SectionTitle>Marketplaces</SectionTitle>
            </Row>
            <Row>
                <InfoPhrase>
                    Escolha onde quer partilhar este veículo. A publicação corre
                    em segundo plano e o estado atualiza-se automaticamente.
                </InfoPhrase>
            </Row>
            <SectionSeparator />
            <ResponsiveRow style={{ alignItems: 'flex-start' }}>
                {PLATFORMS.map((platform) => {
                    const listing = getListing(platform)
                    const status = listing?.status
                    const isPending =
                        status === MarketplaceListingStatusEnum.PENDING
                    const isPublished =
                        status === MarketplaceListingStatusEnum.PUBLISHED
                    return (
                        <Column key={platform} style={{ marginBottom: 16 }}>
                            <PlatformName>
                                {TranslationUtils.getMarketplacePlatformTranslation(
                                    platform
                                )}
                            </PlatformName>
                            <StatusRow>
                                {TranslationUtils.getMarketplaceStatusTranslation(
                                    status
                                )}
                            </StatusRow>
                            {listing?.lastError && (
                                <ErrorText>{listing.lastError}</ErrorText>
                            )}
                            <PrimaryButton
                                type="button"
                                isLoading={isPending}
                                disabled={isPending}
                                redTheme={
                                    MARKETPLACE_FEATURE_ENABLED && isPublished
                                }
                                greenTheme={
                                    MARKETPLACE_FEATURE_ENABLED && !isPublished
                                }
                                label={isPublished ? 'Despublicar' : 'Publicar'}
                                onClick={() => handleActionClick(platform)}
                                style={{
                                    alignSelf: 'flex-start',
                                    ...(MARKETPLACE_FEATURE_ENABLED
                                        ? {}
                                        : {
                                              backgroundColor:
                                                  Colors['light-grey-blue'],
                                              color: Colors.white,
                                              cursor: 'not-allowed',
                                          }),
                                }}
                            />
                        </Column>
                    )
                })}
            </ResponsiveRow>
        </Section>
    )
}

export default PlatformListingsSection

const ResponsiveRow = styled(Row as any)`
    ${Column} {
        width: fit-content;
    }
    ${Column}:not(:last-child) {
        margin-right: 48px;
    }

    @media screen and (max-width: 767px) {
        flex-direction: column;
    }
`

const PlatformName = styled('div')`
    ${Typography['Body_Text_#2_High_Contrast_Left']}
    margin-bottom: 4px;
`

const StatusRow = styled('div')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
    margin-bottom: 8px;
`

const ErrorText = styled('div')`
    ${Typography.Tiny_Text_Regular_Left}
    color: ${Colors['alive-red']};
    font-size: 12px;
    margin-bottom: 8px;
`
